import { Asset, DividendEvent } from '../types';
import { calculateForwardAnnualDividendFromForecast } from './dividendForecast';

export const MIN_DIVIDEND_HISTORY_DAYS_FOR_ANNUALIZATION = 90;

export type TrailingDividendStatus = 'available' | 'insufficient_history' | 'no_dividends';

export interface PortfolioMetricInput {
    holdings: Asset[];
    range: { start: Date; end: Date };
    calculateAssetMarketValue: (asset: Asset) => number;
}

export interface PortfolioMetrics {
    totalValue: number;
    totalInvested: number;
    totalDividendsReceived: number;
    totalProfit: number;
    totalReturnPct: number;
    trailingAnnualDividend: number | null;
    trailingDividendStatus: TrailingDividendStatus;
    historicalYieldOnCost: number | null;
}

export interface PassiveIncomeMetrics {
    forwardAnnualDividend: number;
    passiveIncomeYield: number;
    trailingAnnualDividend: number | null;
    trailingDividendStatus: TrailingDividendStatus;
    historicalYieldOnCost: number | null;
}

export const getDividendHistoryDays = (range: { start: Date; end: Date }) => {
    const start = range.start.getTime();
    const end = range.end.getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0;
    return Math.max(1, (end - start) / (1000 * 3600 * 24));
};

export const calculatePortfolioMetrics = ({
    holdings,
    range,
    calculateAssetMarketValue,
}: PortfolioMetricInput): PortfolioMetrics => {
    const totalValue = holdings.reduce((sum, asset) => sum + calculateAssetMarketValue(asset), 0);
    const totalInvested = holdings.reduce((sum, asset) => sum + (asset.totalInvested || 0), 0);
    const totalDividendsReceived = holdings.reduce((sum, asset) => sum + (asset.totalDividends || 0), 0);
    const totalProfit = totalValue - totalInvested;
    const totalReturnPct = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;
    const dividendHistoryDays = getDividendHistoryDays(range);

    let trailingAnnualDividend: number | null = null;
    let trailingDividendStatus: TrailingDividendStatus = totalDividendsReceived > 0 ? 'insufficient_history' : 'no_dividends';

    if (totalDividendsReceived > 0 && dividendHistoryDays >= MIN_DIVIDEND_HISTORY_DAYS_FOR_ANNUALIZATION) {
        trailingAnnualDividend = totalDividendsReceived / (dividendHistoryDays / 365);
        trailingDividendStatus = 'available';
    }

    const historicalYieldOnCost =
        trailingAnnualDividend !== null && totalInvested > 0 ? (trailingAnnualDividend / totalInvested) * 100 : null;

    return {
        totalValue,
        totalInvested,
        totalDividendsReceived,
        totalProfit,
        totalReturnPct,
        trailingAnnualDividend,
        trailingDividendStatus,
        historicalYieldOnCost,
    };
};

export const calculatePassiveIncomeMetrics = (
    portfolioMetrics: Pick<PortfolioMetrics, 'totalValue' | 'trailingAnnualDividend' | 'trailingDividendStatus' | 'historicalYieldOnCost'>,
    forwardEvents: DividendEvent[],
    now = new Date()
): PassiveIncomeMetrics => {
    const forwardAnnualDividend = calculateForwardAnnualDividendFromForecast(forwardEvents, now);
    const passiveIncomeYield = portfolioMetrics.totalValue > 0 ? (forwardAnnualDividend / portfolioMetrics.totalValue) * 100 : 0;

    return {
        forwardAnnualDividend,
        passiveIncomeYield,
        trailingAnnualDividend: portfolioMetrics.trailingAnnualDividend,
        trailingDividendStatus: portfolioMetrics.trailingDividendStatus,
        historicalYieldOnCost: portfolioMetrics.historicalYieldOnCost,
    };
};
